import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  QrCode, 
  AlertCircle, 
  CheckCircle, 
  Trash2,
  Download,
  Shield,
  List
} from 'lucide-react';
import { hasPageAccess } from '../utils/permissions';

const QRCodes = () => {
  const { user, token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');
  const [url, setUrl] = useState('');
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [generatedQR, setGeneratedQR] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  const tabs = [
    { id: 'generate', label: 'Generate QR Code', icon: QrCode },
    { id: 'list', label: 'QR Code List', icon: List }
  ];

  // Fetch QR codes list
  const fetchQRCodes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/qr-codes`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setQrCodes(data);
      } else if (response.status === 403) {
        setMessage({ type: 'error', text: 'Access denied. Admin or Manager privileges required.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch QR codes.' });
      }
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  // Authentication useEffect
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (token && !user) {
      return;
    }

    if (!token || !user) {
      setPageLoading(false);
      return;
    }

    if (!hasPageAccess(user, 'business.qr_codes', ['admin', 'manager'])) {
      setPageLoading(false);
      return;
    }

    // User has access - fetch QR codes
    fetchQRCodes();
    setPageLoading(false);
  }, [token, user, authLoading]);

  // Load QR codes when list tab is active
  useEffect(() => {
    if (activeTab === 'list' && user && hasPageAccess(user, 'business.qr_codes', ['admin', 'manager'])) {
      fetchQRCodes();
    }
  }, [activeTab, user, token]);

  // Check if user has access
  if (!authLoading && !hasPageAccess(user, 'business.qr_codes', ['admin', 'manager'])) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access QR Codes.</p>
        </div>
      </div>
    );
  }

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading QR Codes...</p>
        </div>
      </div>
    );
  }

  // Handle QR code generation
  const handleGenerateQR = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setMessage({ type: 'error', text: 'Please enter a URL' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    setGeneratedQR(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/qr-codes/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ url: url.trim() })
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedQR({
          url: data.url,
          image: data.qr_code_image,
          id: data.id
        });
        setMessage({ type: 'success', text: data.message });
        setUrl('');
        // Refresh list if on list tab
        if (activeTab === 'list') {
          fetchQRCodes();
        }
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
      setTimeout(() => {
        if (message.type === 'error') {
          setMessage({ type: '', text: '' });
        }
      }, 5000);
    }
  };

  // Download QR code
  const downloadQRCode = (base64Image, url) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64Image}`;
    const filename = `qr-code-${new Date().getTime()}.png`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Delete QR code
  const handleDelete = async (qrCodeId) => {
    if (!window.confirm('Are you sure you want to delete this QR code?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/qr-codes/${qrCodeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'QR code deleted successfully!' });
        fetchQRCodes();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete QR code' });
      }
    } catch (error) {
      console.error('Error deleting QR code:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Inside the main card */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                QR Codes
              </h2>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg flex items-center ${
                message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                )}
                <span>{message.text}</span>
              </div>
            )}

            {/* Tab Navigation - Styled like Business Info */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-3 sm:p-6">
                {/* Generate Tab */}
                {activeTab === 'generate' && (
                  <div className="space-y-6">
                    <form onSubmit={handleGenerateQR} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter URL for QR Code
                        </label>
                        <input
                          type="url"
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          placeholder="https://example.com/your-page"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Enter any URL to generate a scannable QR code
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-theme-blue text-white rounded-lg hover:bg-theme-blue disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <QrCode className="h-4 w-4 mr-2" />
                            Generate QR Code
                          </>
                        )}
                      </button>
                    </form>

                    {/* Generated QR Code Display */}
                    {generatedQR && (
                      <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated QR Code</h3>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-2">URL</label>
                            <input
                              type="text"
                              value={generatedQR.url}
                              readOnly
                              className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700"
                            />
                          </div>

                          <div className="flex flex-col items-center">
                            <label className="block text-sm font-medium text-gray-600 mb-2">QR Code Image</label>
                            <img
                              src={`data:image/png;base64,${generatedQR.image}`}
                              alt="Generated QR Code"
                              className="w-64 h-64 border border-gray-300 rounded"
                            />
                            <button
                              onClick={() => downloadQRCode(generatedQR.image, generatedQR.url)}
                              className="mt-4 px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-theme-blue flex items-center"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download QR Code
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* List Tab */}
                {activeTab === 'list' && (
                  <div>
                    {qrCodes.length === 0 ? (
                      <div className="text-center py-12">
                        <QrCode className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No QR codes generated yet</p>
                        <button
                          onClick={() => setActiveTab('generate')}
                          className="mt-4 text-theme-blue hover:text-blue-700 font-medium"
                        >
                          Generate your first QR code
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {qrCodes.map((qrCode) => (
                          <div key={qrCode.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex flex-col items-center">
                              <img
                                src={`data:image/png;base64,${qrCode.qr_code_image}`}
                                alt="QR Code"
                                className="w-48 h-48 border border-gray-300 rounded mb-3"
                              />
                              <p className="text-sm text-gray-700 mb-2 text-center break-all">
                                {qrCode.url}
                              </p>
                              <p className="text-xs text-gray-500 mb-3">
                                {formatDate(qrCode.created_at)}
                              </p>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => downloadQRCode(qrCode.qr_code_image, qrCode.url)}
                                  className="px-3 py-1 bg-theme-blue text-white rounded hover:bg-theme-blue flex items-center text-sm"
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  Download
                                </button>
                                <button
                                  onClick={() => handleDelete(qrCode.id)}
                                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 flex items-center text-sm"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodes;
